/**
 * Pure geometry for the guided tour — kept free of DOM access so the
 * placement rules are unit-testable.
 */

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** The spotlight cutout: the target rect grown by `padding` on every side. */
export function spotlightRect(target: Rect, padding: number): Rect {
  return {
    top: target.top - padding,
    left: target.left - padding,
    width: target.width + padding * 2,
    height: target.height + padding * 2,
  };
}

export interface CardPlacement {
  /** Vertical anchor: below the cutout, above it, or pinned to the bottom. */
  position: 'below' | 'above' | 'bottom';
  /** CSS `top` for 'below'/'above' (already accounts for card height when
      above); undefined for 'bottom' (which pins via `bottom`). */
  top?: number;
}

/**
 * Where the step card goes relative to the spotlight. Prefer under the
 * cutout (reads like a caption), fall back to above, and when neither fits
 * pin to the bottom edge — the cutout is still fully visible there because
 * scroll choreography parks targets in the upper half of the viewport.
 */
export function placeCard(
  cutout: Rect,
  cardHeight: number,
  viewportHeight: number,
  gap = 14,
  edge = 12,
): CardPlacement {
  const below = cutout.top + cutout.height + gap;
  if (below + cardHeight + edge <= viewportHeight) {
    return { position: 'below', top: below };
  }
  const above = cutout.top - gap - cardHeight;
  if (above >= edge) {
    return { position: 'above', top: above };
  }
  return { position: 'bottom' };
}

/**
 * The window scrollY that parks the target just below the sticky header.
 * Small targets get centered in the space under the header instead, which
 * reads calmer than hugging the header edge.
 */
export function scrollDestination(
  targetTopInDoc: number,
  targetHeight: number,
  headerBottom: number,
  viewportHeight: number,
  documentHeight: number,
  margin = 16,
): number {
  const visibleSpace = viewportHeight - headerBottom;
  // Leave room for the card under short targets: park them a third of the
  // way down the visible space rather than dead-center.
  const lead =
    targetHeight + margin * 2 < visibleSpace
      ? Math.max(margin, (visibleSpace - targetHeight) / 3)
      : margin;
  const wanted = targetTopInDoc - headerBottom - lead;
  return Math.min(
    Math.max(0, wanted),
    Math.max(0, documentHeight - viewportHeight),
  );
}

/** Ease-in-out cubic — the scroll animation's timing curve. */
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
